import GenderSelect from './gender-select';
import SocialSignOn from './social-sign-on';
import EmailPassword from './email-password';
import Iovation from './iovation';
import UiTracking from '../tracking/ui-tracking';
import { DynamicConf } from '@parship/psg-interfaces';
import Turnstile from './turnstile';
import GoogleEnhancedConversions from './google-enhanced-conversions';
import Terms from './terms';

export default class Form {
	private readonly form: HTMLFormElement;
	private readonly genderSelect: GenderSelect;
	private dynamicConf: DynamicConf;
	private socialSignOn: SocialSignOn;
	private emailPassword: EmailPassword;
	private uiTrackingEnabled: boolean;
	private turnstile: Turnstile;
	private terms: Terms;

	public constructor(container: HTMLFormElement) {
		this.form = container;
		this.dynamicConf = window.dynamicConf;
		this.uiTrackingEnabled = this.form.dataset.biEnabled === 'true';

		if (this.dynamicConf?.isIovationActive) {
			new Iovation(container, this.dynamicConf.brandName);
		}

		if (
			this.dynamicConf?.turnstile?.isEnabled &&
			this.form.dataset.cfTurnstile === 'true' &&
			!window.navigator.userAgent.startsWith('PEG')
		) {
			this.turnstile = new Turnstile(this.form, this.dynamicConf.turnstile.siteKey);
		}

		this.form.addEventListener('submit', (event: Event) => this.onSubmit(event));
		this.emailPassword = new EmailPassword(this.form);

		if (!this.form.classList.contains('no-gender-select')) {
			this.genderSelect = new GenderSelect(
				container,
				this.form.classList.contains('preselect'),
				this.form.classList.contains('hetero'),
				() => this.onSexInput()
			);
		}

		this.terms = new Terms(this.form, this.dynamicConf, !!this.genderSelect);
		this.socialSignOn = new SocialSignOn(this.form, this.genderSelect, this.dynamicConf);

		if (
			this.form.classList.contains('show-complete-form') ||
			(this.form.classList.contains('mobile-show-complete-form') && window.innerWidth < 768)
		) {
			this.showCompleteForm();
		}

		if (this.form.classList.contains('mobile-hide-complete-form') && window.innerWidth >= 768) {
			this.genderSelect?.enableUi();
		}

		window.addEventListener('message', (event: MessageEvent) => {
			if (event.origin === window.location.origin && event.data === 'reset-modal') {
				this.form.reset();
				this.emailPassword.hideAllErrors();
				this.genderSelect?.hideError();
			}
		});
	}

	private onSexInput(): void {
		this.showCompleteForm();
		if (this.genderSelect.isValidSex()) {
			this.socialSignOn.setAppleButtonHref(this.genderSelect.getSelectedSex());
			this.socialSignOn.setGoogleButtonHref(this.genderSelect.getSelectedSex());
		}
	}

	private validateBeforeSubmit(): boolean {
		let isValid: boolean = true;

		if (this.genderSelect?.validate() === false) {
			isValid = false;
		}

		if (!this.emailPassword.validate()) {
			isValid = false;
		}

		if (this.turnstile?.validate() === false) {
			isValid = false;
		}

		return isValid;
	}

	private showCompleteForm(): void {
		this.genderSelect?.enableUi();
		this.emailPassword.enableUi();
		this.turnstile?.enableUi();
		this.socialSignOn.enableUi();
		this.terms.enableUi();

		this.form.classList.add('show-complete-form');

		window.setTimeout(() => this.form.classList.add('form-is-complete-visible'), 400);
	}

	private onSubmit(event: Event): void {
		if (!this.form.classList.contains('show-complete-form')) {
			event.preventDefault();
			this.showCompleteForm();
			return;
		}

		if (!this.validateBeforeSubmit()) {
			event.preventDefault();
			return;
		}

		if (this.uiTrackingEnabled) {
			UiTracking.trackUIEvent('registration-form', 'form', 'submit-success');
		}

		GoogleEnhancedConversions.trackConversion(this.form);
	}
}
